import React, { useEffect } from 'react';
import PageHeading from '../components/PageHeading';
import {
  IconAlert,
  IconCalculator,
  IconChart,
  IconChevronRight,
  IconHedge,
  IconLock,
  IconScale,
  IconShield,
  IconTarget,
  IconTrendDown,
} from '../components/Icons';
import { trackEvent } from '../utils/analytics';
import './RiskToolsPage.css';

// Trade Watcher covers every account (NinjaTrader and brokers), so it sits on
// top; then the portfolio tools, then the prop firm tools (fewer users now).
const TRADE_WATCHER = {
  id: 'trade-watcher',
  title: 'Trade Watcher',
  description: 'Watch your NinjaTrader and brokerage accounts live and get alerted when a position is left without a stop.',
  Icon: IconShield,
  page: 'trade-watcher',
  feature: 'trade_watcher',
};

const PORTFOLIO_TOOLS = [
  { id: 'position-sizer', title: 'Position Sizer', description: 'How many shares to buy so a stop-out costs only what you choose to risk.', Icon: IconCalculator, page: 'position-sizer', free: true },
  { id: 'concentration-risk', title: 'Concentration Risk', description: 'How much of your money rides on a few holdings, and what a market drop would cost.', Icon: IconAlert, page: 'concentration-risk' },
  { id: 'drawdown-risk', title: 'Drawdown & VaR', description: 'How far your accounts fall from their highs, how much they swing, and a typical bad day.', Icon: IconChart, page: 'drawdown-risk' },
  { id: 'stop-planner', title: 'Stop Planner', description: 'Plan a stop for each holding and see what you’d lose, or lock in, if they’re hit.', Icon: IconShield, page: 'stop-planner' },
  { id: 'rebalance', title: 'Rebalancing', description: 'Set target weights and get the buys and sells that bring your holdings back in line.', Icon: IconScale, page: 'rebalance' },
];

const PROP_FIRM_TOOLS = [
  {
    id: 'blowout-risk',
    title: 'Blowout Risk',
    description: 'Estimate how much capital you need to survive drawdowns and avoid blowing your account.',
    Icon: IconTrendDown,
    mode: 'risk',
    feature: 'blowout_risk',
  },
  {
    id: 'rule-risk',
    title: '% Rule Risk',
    description: 'Check consistency-rule and trailing-drawdown risk using Apex-style MAE analysis.',
    Icon: IconScale,
    mode: 'apexMae',
    feature: 'rule_risk',
  },
  {
    id: 'hedging-risk',
    title: 'Hedging Risk',
    description: 'Find strategy pairs that may hedge or offset each other based on live trade overlap.',
    Icon: IconHedge,
    page: 'hedging-risk',
    feature: 'hedging_risk',
  },
  {
    id: 'prop-firm-planner',
    title: 'Prop Firm Planner',
    description: 'Pick your firm and account: see which strategy and size passed that evaluation most often.',
    Icon: IconTarget,
    page: 'prop-firm-planner',
    feature: 'prop_firm_planner',
  },
];

/** Risk Tools on mobile. Tools hidden in Users → Feature access are left out. */
function RiskToolsPage({
  onNavigate,
  onModeChange,
  isPro = false,
  canSeeFeature = (key) => key !== 'trade_watcher',
  isFeatureLocked = () => false,
}) {
  const visible = (tool) => !tool.feature || canSeeFeature(tool.feature);
  const locked = (tool) => Boolean(tool.feature) && isFeatureLocked(tool.feature);
  useEffect(() => {
    trackEvent('risk_tools_hub_opened', { is_pro: isPro });
  }, [isPro]);

  const handleSelect = (tool) => {
    trackEvent('risk_tools_hub_selected', { tool_id: tool.id, is_pro: isPro });
    if (tool.page) {
      onNavigate(tool.page);
      return;
    }
    onNavigate('home');
    onModeChange(tool.mode);
  };

  return (
    <div className="risk-tools-page">
      <PageHeading
        title="Risk Tools"
        description="Watch your accounts live, or pick a calculator for your portfolio or prop firm account."
      />

      {[
        { key: 'top', title: null, tools: [TRADE_WATCHER] },
        { key: 'portfolio', title: 'Portfolio', note: 'Stocks, ETFs and crypto in your brokerage accounts', tools: PORTFOLIO_TOOLS },
        { key: 'prop', title: 'Prop firm', note: 'Futures traders working toward or trading a funded account', tools: PROP_FIRM_TOOLS },
      ].map((group) => {
        const tools = group.tools.filter(visible);
        if (!tools.length) return null;
        return (
          <section key={group.key} className="risk-tools-group">
            {group.title ? (
              <h2 className="risk-tools-group-title">
                {group.title}
                <span>{group.note}</span>
              </h2>
            ) : null}
            <ul className="risk-tools-list">
              {tools.map((tool) => {
                const { Icon } = tool;
                return (
            <li key={tool.id}>
              <button
                type="button"
                className={`risk-tools-card${locked(tool) ? ' is-locked' : ''}`}
                onClick={() => handleSelect(tool)}
              >
                <span className="risk-tools-card-icon" aria-hidden="true">
                  <Icon size={22} />
                </span>
                <span className="risk-tools-card-body">
                  <span className="risk-tools-card-title-row">
                    <strong>{tool.title}</strong>
                    {locked(tool) ? (
                      <span className="risk-tools-lock-badge" aria-label="Locked">
                        <IconLock size={14} />
                      </span>
                    ) : null}
                    {!isPro && !tool.free && !locked(tool) ? (
                      <span className="risk-tools-pro-badge" aria-label="Pro feature">
                        PRO
                      </span>
                    ) : null}
                  </span>
                  <span className="risk-tools-card-desc">{tool.description}</span>
                </span>
                <span className="risk-tools-card-chevron" aria-hidden="true">
                  <IconChevronRight size={18} />
                </span>
              </button>
            </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export default RiskToolsPage;
